const nas_string = '.'

const Strength = {
  Normal: 'Normal',
  VeryStrong: 'VeryStrong',
  Strong: 'Strong',
  Moderate: 'Moderate',
  Supporting: 'Supporting',
  Unmet: 'Unmet'
}

const Evidence_Types = {
  VeryStrong: ['PVS1', 'BA1'],
  Strong: ['PS1', 'PS2', 'PS3', 'PS4', 'BS1', 'BS2', 'BS3', 'BS4'],
  Moderate: ['PM1', 'PM2', 'PM3', 'PM4', 'PM5', 'PM6'],
  Supporting: [
    'PP1',
    'PP2',
    'PP3',
    'PP4',
    'PP5',
    'BP1',
    'BP2',
    'BP3',
    'BP4',
    'BP5',
    'BP6',
    'BP7'
  ]
}

const Pathogenic_Evidences = [
  'PVS1',
  'PS1',
  'PS2',
  'PS3',
  'PS4',
  'PM1',
  'PM2',
  'PM3',
  'PM4',
  'PM5',
  'PM6',
  'PP1',
  'PP2',
  'PP3',
  'PP4',
  'PP5'
]

const Benign_Evidences = [
  'BA1',
  'BS1',
  'BS2',
  'BS3',
  'BS4',
  'BP1',
  'BP2',
  'BP3',
  'BP4',
  'BP5',
  'BP6',
  'BP7'
]

// 辅助函数：根据强度获取证据列表（需确保以下全局变量已定义）
function get_evidences_list_by_strength(
  evidences: any[],
  strength: string,
  type = 'pathogenic'
) {
  const possible_list =
    type === 'pathogenic' ? Pathogenic_Evidences : Benign_Evidences

  return evidences.filter(([ev, ev_strength]) => {
    if (!possible_list.includes(ev)) return false

    return (
      ev_strength === strength ||
      (Evidence_Types[strength as keyof typeof Evidence_Types]?.includes(ev) &&
        ev_strength === nas_string)
    )
  })
}

// 主分类函数
export function classify(evidences: string[]) {
  const BPS = [
    'Pathogenic',
    'Likely pathogenic',
    'Benign',
    'Likely benign',
    'Uncertain significance'
  ]

  // 初始化证据标记
  const PVS1 = evidences.includes('PVS1') ? 1 : 0
  const BA1 = evidences.includes('BA1') ? 1 : 0
  const BP7 = evidences.includes('BP7') ? 1 : 0
  const BS1 = evidences.includes('BS1') ? 1 : 0
  const PM2_Supporting = evidences.includes('PM2_Supporting') ? 1 : 0

  // 转换证据格式
  const processedEvidences = evidences.map((ev: string) => {
    return ev.includes('_')
      ? [ev.split('_')[0], ev.split('_')[1]]
      : [ev, nas_string]
  })

  // 获取不同强度的证据列表
  const PVS = get_evidences_list_by_strength(
    processedEvidences,
    Strength.VeryStrong
  )
  const PS = get_evidences_list_by_strength(processedEvidences, Strength.Strong)
  const PM = get_evidences_list_by_strength(
    processedEvidences,
    Strength.Moderate
  )
  const PP = get_evidences_list_by_strength(
    processedEvidences,
    Strength.Supporting
  )

  const BS = get_evidences_list_by_strength(
    processedEvidences,
    Strength.Strong,
    'benign'
  )
  const BP = get_evidences_list_by_strength(
    processedEvidences,
    Strength.Supporting,
    'benign'
  )

  // 计算证据数量
  const PVS_sum = PVS.length
  const PS_sum = PS.length
  const PM_sum = PM.length
  const PP_sum = PP.length
  const BS_sum = BS.length
  const BP_sum = BP.length

  // 特殊情况处理
  try {
    if (
      PVS1 === 1 &&
      PVS_sum === 1 &&
      PS_sum === 0 &&
      PM_sum === 0 &&
      PP_sum === 1 &&
      BA1 === 0 &&
      BS_sum === 0 &&
      BP_sum === 0
    ) {
      if (PM2_Supporting === 1) return BPS[1]
    }
  } catch (e) {
    /* 空catch块 */
  }

  // 主逻辑判断
  if (
    (PVS_sum > 0 || PS_sum > 0 || PM_sum > 0 || PP_sum > 0) &&
    (BA1 === 1 || BS_sum > 0 || BP_sum > 0)
  ) {
    return BPS[4]
  }

  if (PVS_sum > 1) return BPS[0]

  // Benign 条件
  if (
    (BP7 === 1 && BP_sum >= 3) ||
    BA1 === 1 ||
    BS_sum > 1 ||
    (BS_sum >= 1 && BP_sum >= 3) ||
    (BP_sum > 2 && BP7 === 1) ||
    (BS_sum === 1 && BP_sum === 2 && BP7 === 1)
  ) {
    return BPS[2]
  }

  // Pathogenic 条件
  if (
    (PVS_sum === 1 && PS_sum > 0) ||
    (PVS_sum === 1 && PM_sum > 1) ||
    (PVS_sum === 1 && PM_sum > 0 && PP_sum > 0) ||
    (PVS_sum === 1 && PP_sum > 1) ||
    PS_sum > 1 ||
    (PS_sum > 0 && PM_sum > 2) ||
    (PS_sum > 0 && PM_sum > 1 && PP_sum > 1) ||
    (PS_sum > 0 && PM_sum > 0 && PP_sum > 3)
  ) {
    return BPS[0]
  }

  // Likely pathogenic 条件
  if (
    (PVS_sum === 1 && PM_sum === 1) ||
    (PS_sum === 1 && PM_sum >= 1 && PM_sum < 3) ||
    (PS_sum === 1 && PP_sum >= 2) ||
    PM_sum > 2 ||
    (PM_sum > 1 && PP_sum > 1) ||
    (PM_sum > 0 && PP_sum > 3)
  ) {
    return BPS[1]
  }

  // Likely benign 条件
  if (
    // P6 条件
    (!(!BP7 || BP_sum < 3) &&
      !BA1 &&
      BS_sum <= 1 &&
      BS1 &&
      BS_sum >= 1 &&
      BS_sum < 3) ||
    // eslint-disable-next-line lines-around-comment
    // P7 条件
    (!BA1 &&
      BS_sum <= 1 &&
      (BS_sum === 0 || BP_sum < 3) &&
      (BP_sum <= 2 || !BP7) &&
      (BS_sum !== 1 || BP_sum !== 2 || !BP7) &&
      BS_sum === 1 &&
      BP_sum >= 1 &&
      BP_sum < 3) ||
    // eslint-disable-next-line lines-around-comment
    // P8 条件
    (!BA1 &&
      BS_sum <= 1 &&
      (BS_sum === 0 || BP_sum < 3) &&
      (BP_sum <= 2 || !BP7) &&
      (BS_sum !== 1 || BP_sum !== 2 || !BP7) &&
      BP_sum >= 2)
  ) {
    return BPS[3]
  }

  return BPS[4]
}
